import { useEffect, useState } from 'react'
import { Pencil, Save, X, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import { PageLayout } from '@/components/layout/PageLayout'
import { notify } from '@/lib/notify'
import {
  useGetApiCompaniesCurrentCustomizations,
  usePatchApiCompaniesCurrentCustomizationsColor,
  usePatchApiCompaniesCurrentCustomizationsLogo,
  getGetApiCompaniesCurrentCustomizationsQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import { useQueryClient } from '@tanstack/react-query'
import { AppDesignLogo } from './components/AppDesignLogo'
import { AppDesignColor } from './components/AppDesignColor'
import { AppDesignPreview } from './components/AppDesignPreview'
import { PageCard } from '@/components/common/PageCard'
import { PageHeader } from '@/components/layout/PageHeader'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

export default function AppDesignPage() {
  const { t } = useTranslation(['configuration', 'common'])
  const queryClient = useQueryClient()

  const { data: customizationsData, isLoading, isError } = useGetApiCompaniesCurrentCustomizations()
  const saved = customizationsData?.data

  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [draftLogoUri, setDraftLogoUri] = useState<string | null>(null)
  const [draftLogoFile, setDraftLogoFile] = useState<File | null>(null)
  const [overrideLogo, setOverrideLogo] = useState(false)

  const [draftAccentColor, setDraftAccentColor] = useState<string | null>(null)
  const [overrideAccentColor, setOverrideAccentColor] = useState(false)

  // Sync drafts when saved values arrive (outside edit mode)
  useEffect(() => {
    if (!isEditMode) {
      setDraftLogoUri(saved?.companyLogo ?? null)
      setDraftAccentColor(saved?.accentColor ?? null)
    }
  }, [saved, isEditMode])

  const { mutateAsync: patchColor } = usePatchApiCompaniesCurrentCustomizationsColor()
  const { mutateAsync: patchLogo } = usePatchApiCompaniesCurrentCustomizationsLogo()

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentCustomizationsQueryKey() })

  // ─── Logo handlers ──────────────────────────────────────────────────────────
  const handleLogoSelected = (file: File, dataUrl: string) => {
    setDraftLogoUri(dataUrl)
    setDraftLogoFile(file)
    setOverrideLogo(true)
  }
  const handleResetLogo = () => {
    setDraftLogoUri(saved?.companyLogo ?? null)
    setDraftLogoFile(null)
    setOverrideLogo(false)
  }
  const handleDeleteLogo = () => {
    setDraftLogoUri(null)
    setDraftLogoFile(null)
    setOverrideLogo(saved?.companyLogo != null)
  }

  // ─── Color handlers ─────────────────────────────────────────────────────────
  const handleColorChange = (hex: string) => {
    setDraftAccentColor(hex)
    setOverrideAccentColor(true)
  }
  const handleResetColor = () => {
    setDraftAccentColor(saved?.accentColor ?? null)
    setOverrideAccentColor(false)
  }
  const handleDeleteColor = () => {
    setDraftAccentColor(null)
    setOverrideAccentColor(saved?.accentColor != null)
  }

  // ─── General ────────────────────────────────────────────────────────────────
  const handleEdit = () => setIsEditMode(true)

  const handleCancel = () => {
    setIsEditMode(false)
    setDraftLogoUri(saved?.companyLogo ?? null)
    setDraftLogoFile(null)
    setDraftAccentColor(saved?.accentColor ?? null)
    setOverrideLogo(false)
    setOverrideAccentColor(false)
  }

  const handleDeleteAll = () => {
    setDraftLogoUri(null)
    setDraftLogoFile(null)
    setDraftAccentColor(null)
    setOverrideLogo(saved?.companyLogo != null)
    setOverrideAccentColor(saved?.accentColor != null)
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      if (overrideAccentColor) {
        await patchColor({ data: { colorHex: draftAccentColor } })
      }
      if (overrideLogo && draftLogoFile) {
        await patchLogo({ data: { File: draftLogoFile } })
      }
      await invalidate()
      setOverrideLogo(false)
      setOverrideAccentColor(false)
      setDraftLogoFile(null)
      setIsEditMode(false)
      notify.success(t('toasts.saved'))
    } catch (error) {
      // Stay in edit mode so nothing gets lost.
      notify.apiError(error, t('toasts.saveError'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <PageLayout scrollEnabled>
      <PageHeader
        title={t('header.title')}
        description={t('header.description')}>
      </PageHeader>

      <PageCard>
        <LoaderGeneral isLoading={isLoading} isError={isError}>
          <div className="flex flex-wrap gap-12 items-start">
            <AppDesignLogo
              isEditMode={isEditMode}
              isSaving={isSaving}
              logoUrl={draftLogoUri}
              onLogoSelected={handleLogoSelected}
              onResetClick={handleResetLogo}
              onDeleteClick={handleDeleteLogo}
            />
            <AppDesignPreview
              accentColor={draftAccentColor}
              logoUrl={draftLogoUri}
            />
            <AppDesignColor
              isEditMode={isEditMode}
              isSaving={isSaving}
              accentColor={draftAccentColor}
              onColorChange={handleColorChange}
              onResetClick={handleResetColor}
              onDeleteClick={handleDeleteColor}
            />
          </div>

          <Separator className="mt-auto mb-4" />

          <div className="flex items-center gap-2">
            {!isEditMode ? (
              <Button onClick={handleEdit}>
                <Pencil className="size-4" />
                {t('buttons.edit', { ns: 'common' })}
              </Button>
            ) : (
              <>
                <Button onClick={handleSave} disabled={isSaving}>
                  <Save className="size-4" />
                  {isSaving ? t('buttons.saving', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
                </Button>
                <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                  <X className="size-4" />
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button variant="destructive" onClick={handleDeleteAll} disabled={isSaving}>
                  <Trash2 className="size-4" />
                  {t('actions.deleteAll')}
                </Button>
              </>
            )}
          </div>
        </LoaderGeneral>
      </PageCard>
    </PageLayout>
  )
}
